import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    IconButton,
    Tooltip,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import CreateIcon from '@mui/icons-material/Create';
import CircleIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

// Tool types
const TOOLS = {
    ARROW: 'arrow',
    TEXT: 'text',
    ELLIPSE: 'ellipse',
    FREEHAND: 'freehand',
};

/**
 * ScreenshotEditor - Komponente zum Bearbeiten von Screenshots mit Annotationen
 */
const ScreenshotEditor = ({
    screenshotUrl,
    onSave,
    onCancel,
    isClassic
}) => {
    const [activeTool, setActiveTool] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [drawingHistory, setDrawingHistory] = useState([]);
    const [textInput, setTextInput] = useState({
        show: false,
        text: '',
        x: 0,
        y: 0,
    });

    const canvasRef = useRef(null);
    const imageRef = useRef(null);
    const canvasContainerRef = useRef(null);
    const startPointRef = useRef({ x: 0, y: 0 });
    const pathRef = useRef([]);

    // Canvas und Bild einrichten
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.src = screenshotUrl;

        img.onload = () => {
            // Canvas-Dimensionen an das Bild anpassen
            canvas.width = img.width;
            canvas.height = img.height;

            // Bild auf Canvas zeichnen
            ctx.drawImage(img, 0, 0);

            // Bild-Referenz speichern
            imageRef.current = img;
        };
    }, [screenshotUrl]);

    // Canvas mit aktueller Historie neu rendern, wenn sich die Historie ändert
    useEffect(() => {
        if (!canvasRef.current || !imageRef.current) return;

        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');

        // Canvas löschen und Bild neu zeichnen
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(imageRef.current, 0, 0);

        // Alle Elemente aus der Historie neu zeichnen
        drawingHistory.forEach(item => {
            drawItem(ctx, item);
        });
        // eslint-disable-next-line
    }, [drawingHistory]);

    // Berechnet die Canvas-Koordinaten basierend auf Mausposition
    const getCanvasCoordinates = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
            x: (e.clientX - rect.left) * scaleX,
            y: (e.clientY - rect.top) * scaleY
        };
    };

    // Mouse-Down-Event behandeln
    const handleMouseDown = (e) => {
        if (!activeTool || !canvasRef.current) return;

        const coords = getCanvasCoordinates(e);
        startPointRef.current = coords;

        // Für Text-Tool speziell das Texteingabefeld öffnen
        if (activeTool === TOOLS.TEXT) {
            console.log("Text-Tool aktiviert, Textfeld wird an Position geöffnet:", coords);
            setTextInput({
                show: true,
                text: '',
                x: coords.x,
                y: coords.y,
            });
        } else {
            setIsDrawing(true);

            if (activeTool === TOOLS.FREEHAND) {
                pathRef.current = [{ x: coords.x, y: coords.y }];
            }
        }
    };

    // Mouse-Move-Event behandeln
    const handleMouseMove = (e) => {
        if (!isDrawing || !activeTool || activeTool === TOOLS.TEXT || !canvasRef.current) return;

        const coords = getCanvasCoordinates(e);

        // Context holen
        const ctx = canvasRef.current.getContext('2d');

        // Canvas zurücksetzen und Historie neu zeichnen
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.drawImage(imageRef.current, 0, 0);

        // Historie neu zeichnen
        drawingHistory.forEach(item => {
            drawItem(ctx, item);
        });

        // Aktuelles Element vorschau zeichnen
        if (activeTool === TOOLS.FREEHAND) {
            // Punkt zum Pfad hinzufügen
            pathRef.current.push({ x: coords.x, y: coords.y });

            // Pfad zeichnen
            ctx.beginPath();
            ctx.moveTo(pathRef.current[0].x, pathRef.current[0].y);

            for (let i = 1; i < pathRef.current.length; i++) {
                ctx.lineTo(pathRef.current[i].x, pathRef.current[i].y);
            }

            ctx.strokeStyle = 'red';
            ctx.lineWidth = 5; // Dickere Linien
            ctx.stroke();
        } else {
            // Für andere Formen Vorschau erstellen
            const item = createDrawingItem(
                activeTool,
                startPointRef.current.x,
                startPointRef.current.y,
                coords.x,
                coords.y
            );

            if (item) {
                drawItem(ctx, item);
            }
        }
    };

    // Mouse-Up-Event behandeln
    const handleMouseUp = (e) => {
        if (!isDrawing || !activeTool || activeTool === TOOLS.TEXT || !canvasRef.current) return;

        const coords = getCanvasCoordinates(e);

        // Element erstellen und zur Historie hinzufügen
        if (activeTool === TOOLS.FREEHAND) {
            // Freihand-Pfad finalisieren
            if (pathRef.current.length > 1) {
                const newItem = {
                    type: TOOLS.FREEHAND,
                    path: [...pathRef.current],
                    color: 'red',
                    lineWidth: 5, // Dickere Linien
                };
                setDrawingHistory([...drawingHistory, newItem]);
            }
        } else {
            const newItem = createDrawingItem(
                activeTool,
                startPointRef.current.x,
                startPointRef.current.y,
                coords.x,
                coords.y
            );

            if (newItem) {
                setDrawingHistory([...drawingHistory, newItem]);
            }
        }

        setIsDrawing(false);
    };

    // Zeichnungselement basierend auf Werkzeugtyp und Koordinaten erstellen
    const createDrawingItem = (tool, startX, startY, endX, endY) => {
        // Prüfen, ob eine signifikante Zeichenbewegung stattgefunden hat
        const distance = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
        if (distance < 5) return null; // Zu klein, ignorieren

        switch (tool) {
            case TOOLS.ARROW:
                return {
                    type: TOOLS.ARROW,
                    startX,
                    startY,
                    endX,
                    endY,
                    color: 'red',
                    lineWidth: 5, // Dickere Linien
                };
            case TOOLS.ELLIPSE:
                const width = Math.abs(endX - startX);
                const height = Math.abs(endY - startY);
                const centerX = startX + (endX - startX) / 2;
                const centerY = startY + (endY - startY) / 2;
                return {
                    type: TOOLS.ELLIPSE,
                    centerX,
                    centerY,
                    radiusX: width / 2,
                    radiusY: height / 2,
                    color: 'red',
                    lineWidth: 5, // Dickere Linien
                };
            default:
                return null;
        }
    };

    // Element auf Canvas-Context zeichnen
    const drawItem = (ctx, item) => {
        if (!item) return;

        ctx.save();
        ctx.strokeStyle = item.color || 'red';
        ctx.lineWidth = item.lineWidth || 5; // Dickere Linien
        ctx.fillStyle = item.color || 'red';

        switch (item.type) {
            case TOOLS.ARROW:
                drawArrow(ctx, item.startX, item.startY, item.endX, item.endY);
                break;
            case TOOLS.ELLIPSE:
                ctx.beginPath();
                ctx.ellipse(
                    item.centerX,
                    item.centerY,
                    item.radiusX,
                    item.radiusY,
                    0,
                    0,
                    2 * Math.PI
                );
                ctx.stroke();
                break;
            case TOOLS.FREEHAND:
                ctx.beginPath();
                ctx.moveTo(item.path[0].x, item.path[0].y);

                for (let i = 1; i < item.path.length; i++) {
                    ctx.lineTo(item.path[i].x, item.path[i].y);
                }

                ctx.stroke();
                break;
            case TOOLS.TEXT:
                ctx.font = `bold ${item.fontSize || 24}px Arial`; // Größerer Font für bessere Sichtbarkeit
                ctx.fillText(item.text, item.x, item.y);
                break;
            default:
                break;
        }

        ctx.restore();
    };

    // Pfeilform zeichnen
    const drawArrow = (ctx, fromX, fromY, toX, toY) => {
        const headLength = 15;
        const angle = Math.atan2(toY - fromY, toX - fromX);

        // Linie zeichnen
        ctx.beginPath();
        ctx.moveTo(fromX, fromY);
        ctx.lineTo(toX, toY);
        ctx.stroke();

        // Pfeilspitze zeichnen
        ctx.beginPath();
        ctx.moveTo(toX, toY);
        ctx.lineTo(
            toX - headLength * Math.cos(angle - Math.PI / 6),
            toY - headLength * Math.sin(angle - Math.PI / 6)
        );
        ctx.lineTo(
            toX - headLength * Math.cos(angle + Math.PI / 6),
            toY - headLength * Math.sin(angle + Math.PI / 6)
        );
        ctx.closePath();
        ctx.fill();
    };

    // Text-Eingabe-Übermittlung behandeln
    const handleTextSubmit = () => {
        if (textInput.text.trim()) {
            const newItem = {
                type: TOOLS.TEXT,
                x: textInput.x,
                y: textInput.y,
                text: textInput.text,
                color: 'red',
                fontSize: 24, // Größerer Font für bessere Sichtbarkeit
            };

            // Text zum Zeichnungsverlauf hinzufügen
            const newHistory = [...drawingHistory, newItem];
            setDrawingHistory(newHistory);

            // Canvas sofort aktualisieren
            const canvas = canvasRef.current;
            if (canvas) {
                const ctx = canvas.getContext('2d');
                drawItem(ctx, newItem);
            }
        }

        // Texteingabefeld schließen
        setTextInput({ show: true, text: '', x: 200, y: 100 });
    };

    // Rückgängig-Aktion behandeln
    const handleUndo = () => {
        if (drawingHistory.length > 0) {
            setDrawingHistory(drawingHistory.slice(0, -1));
        }
    };

    // Speichern-Aktion behandeln
    const handleSave = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const finalImageUrl = canvas.toDataURL('image/png');
        onSave(finalImageUrl);
    };

    return (
        <Box sx={{ position: 'relative', width: '100%' }}>
            {/* Toolbar */}
            <Paper
                elevation={3}
                sx={{
                    display: 'flex',
                    p: 1,
                    mb: 1,
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1
                }}
            >
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Pfeil zeichnen">
                        <IconButton
                            color={activeTool === TOOLS.ARROW ? 'secondary' : 'default'}
                            onClick={() => {
                                setActiveTool(TOOLS.ARROW);
                                setTextInput({ show: false, text: '', x: 0, y: 0 });
                            }}
                        >
                            <ArrowRightAltIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Text hinzufügen">
                        <IconButton
                            color={activeTool === TOOLS.TEXT ? 'secondary' : 'default'}
                            onClick={() => {
                                setActiveTool(TOOLS.TEXT);
                                console.log("Text-Tool aktiviert");
                            }}
                        >
                            <TextFieldsIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Ellipse zeichnen">
                        <IconButton
                            color={activeTool === TOOLS.ELLIPSE ? 'secondary' : 'default'}
                            onClick={() => {
                                setActiveTool(TOOLS.ELLIPSE);
                                setTextInput({ show: false, text: '', x: 0, y: 0 });
                            }}
                        >
                            <CircleIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Freihand zeichnen">
                        <IconButton
                            color={activeTool === TOOLS.FREEHAND ? 'secondary' : 'default'}
                            onClick={() => {
                                setActiveTool(TOOLS.FREEHAND);
                                setTextInput({ show: false, text: '', x: 0, y: 0 });
                            }}
                        >
                            <CreateIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Rückgängig machen">
                        <span>
                            <IconButton
                                onClick={handleUndo}
                                disabled={drawingHistory.length === 0}
                            >
                                <UndoIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Screenshot verwerfen">
                        <IconButton color="error" onClick={onCancel}>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Screenshot übernehmen">
                        <IconButton color="primary" onClick={handleSave}>
                            <CheckIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Paper>

            {activeTool && (
                <Typography variant="subtitle2" gutterBottom>
                    {activeTool === TOOLS.ARROW ? 'Pfeil zeichnen: ' :
                        activeTool === TOOLS.TEXT ? 'Text hinzufügen: Klicken Sie auf den Screenshot, um Text zu platzieren' :
                            activeTool === TOOLS.ELLIPSE ? 'Ellipse zeichnen: ' :
                                'Freihand zeichnen: '}
                    {activeTool !== TOOLS.TEXT && 'Klicken und ziehen, um zu zeichnen'}
                </Typography>
            )}

            {/* Canvas-Container für Zeichnungen */}
            <Box
                ref={canvasContainerRef}
                sx={{
                    position: 'relative',
                    width: '100%',
                    border: '1px solid #ddd',
                    borderRadius: isClassic ? '4px' : '8px',
                    overflow: 'auto',
                }}
            >
                <canvas
                    ref={canvasRef}
                    style={{ display: 'block', width: '100%', height: 'auto', cursor: activeTool === TOOLS.TEXT ? 'text' : 'crosshair' }}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                />

                {/* Text-Eingabe-Overlay */}
                {textInput.show && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: `${(textInput.x / (canvasRef.current?.width || 1)) * 100}%`,
                            top: `${(textInput.y / (canvasRef.current?.height || 1)) * 100}%`,
                            zIndex: 10,
                            transform: 'translate(-50%, -50%)',
                            bgcolor: 'background.paper',
                            boxShadow: '0 0 10px rgba(0,0,0,0.2)',
                            borderRadius: '4px',
                            p: 1
                        }}
                    >
                        <TextField
                            autoFocus
                            variant="outlined"
                            size="small"
                            value={textInput.text}
                            onChange={(e) => setTextInput({ ...textInput, text: e.target.value })}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleTextSubmit();
                                }
                            }}
                            onBlur={handleTextSubmit}
                            placeholder="Text eingeben..."
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        size="small"
                                        onClick={handleTextSubmit}
                                        sx={{ ml: 1 }}
                                    >
                                        <CheckIcon fontSize="small" />
                                    </IconButton>
                                ),
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ScreenshotEditor;
